"use client";

import { createContext, useCallback, useContext, useEffect } from "react";

import { CONTACT_RECAPTCHA_ACTION } from "./recaptcha-constants";

const SCRIPT_ID = "recaptcha-enterprise";
const SCRIPT_WAIT_TIMEOUT_MS = 15_000;

interface RecaptchaContext {
  executeRecaptcha: (action?: string) => Promise<string>;
}

const RecaptchaCtx = createContext<RecaptchaContext | null>(null);

export const useRecaptcha = (): RecaptchaContext | null => {
  return useContext(RecaptchaCtx);
};

interface Props {
  children: React.ReactNode;
  siteKey: string;
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

/**
 * Ensures enterprise.js is in the document. Never removes it — tearing the
 * script down on unmount races with remounts.
 */
const ensureScript = (siteKey: string): void => {
  if (document.getElementById(SCRIPT_ID)) {
    return;
  }
  const scriptSrc = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = scriptSrc;
  script.async = true;
  script.onerror = () => {
    console.error(
      "contact form: failed to load enterprise.js — check site key is Enterprise",
      scriptSrc,
    );
  };
  document.head.appendChild(script);
};

/** Resolves once window.grecaptcha.enterprise exists. */
const waitForEnterprise = (): Promise<
  NonNullable<Window["grecaptcha"]>["enterprise"]
> =>
  new Promise((resolve, reject) => {
    const existing = window.grecaptcha?.enterprise;
    if (existing) {
      resolve(existing);
      return;
    }

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      const enterprise = window.grecaptcha?.enterprise;
      if (enterprise) {
        clearInterval(intervalId);
        resolve(enterprise);
        return;
      }
      if (Date.now() - startedAt >= SCRIPT_WAIT_TIMEOUT_MS) {
        clearInterval(intervalId);
        reject(new Error("reCAPTCHA script did not load"));
      }
    }, 100);
  });

/**
 * Google's recommended sequence on the user interaction:
 * ready() → execute(siteKey, { action }).
 * @see https://cloud.google.com/recaptcha/docs/instrument-web-pages
 */
const executeEnterprise = async (
  siteKey: string,
  action: string,
): Promise<string> => {
  const enterprise = await waitForEnterprise();
  return new Promise((resolve, reject) => {
    enterprise.ready(() => {
      void enterprise.execute(siteKey, { action }).then(resolve, reject);
    });
  });
};

export function RecaptchaProvider({ children, siteKey }: Props) {
  useEffect(() => {
    ensureScript(siteKey);
  }, [siteKey]);

  const executeRecaptcha = useCallback(
    (action = CONTACT_RECAPTCHA_ACTION): Promise<string> =>
      executeEnterprise(siteKey, action),
    [siteKey],
  );

  return (
    <RecaptchaCtx.Provider value={{ executeRecaptcha }}>
      {children}
    </RecaptchaCtx.Provider>
  );
}
